// src/pages/inventory/commerce/shopify/tabs/ProductSyncTab.tsx
import React, { useState } from 'react';
import { Alert, Space, Row, Col, Statistic, Divider, Typography, Button } from 'antd';
import { SyncOutlined, ImportOutlined, ShoppingOutlined } from '@ant-design/icons';
import { syncProductsToShopify, importProductsFromShopify } from '../../../../api/inventory/shopifyApi';
import { useNotifications } from '../../../../context/NotificationContext';
import { SectionCard } from '../../../../components/shared';

const { Title, Text, Paragraph } = Typography;

const ProductSyncTab: React.FC = () => {
	const [syncing, setSyncing] = useState(false);
	const [importing, setImporting] = useState(false);
	const [lastSyncResult, setLastSyncResult] = useState<any>(null);
	const [lastImportResult, setLastImportResult] = useState<any>(null);
	const { showError, showSuccess, showInfo } = useNotifications();

	const handleSyncProducts = async () => {
		setSyncing(true);
		try {
			const result = await syncProductsToShopify();
			setLastSyncResult(result.results);
			showSuccess(`Products synced successfully! Created: ${result.results.created}, Updated: ${result.results.updated}`);
		} catch (error) {
			showError("Failed to sync products to Shopify");
		} finally {
			setSyncing(false);
		}
	};

	const handleImportProducts = async () => {
		setImporting(true);
		try {
			const result = await importProductsFromShopify({
				import_only: true,
			});
			setLastImportResult(result.results);
			showSuccess(`Products imported successfully! Created: ${result.results.created}, Updated: ${result.results.updated}`);
			showInfo(result.note);
		} catch (error) {
			showError("Failed to import products from Shopify");
		} finally {
			setImporting(false);
		}
	};

	return (
		<div>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				<Alert 
					message="Product Synchronization" 
					description="Sync products between your Xoe Med inventory and Shopify store. You can push products to Shopify or import products from your store." 
					type="info" 
					showIcon 
					icon={<ShoppingOutlined />} 
				/>

				<Row gutter={[16, 16]}>
					<Col xs={24} lg={12}>
						<SectionCard title="Sync to Shopify">
							<Paragraph>
								<Text>Push your Xoe Med products to your Shopify store. This will create or update products in Shopify based on your inventory.</Text>
							</Paragraph>
							<Divider />
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Text type="secondary">Export products to your Shopify store</Text>
								<Button type="primary" icon={<SyncOutlined />} onClick={handleSyncProducts} loading={syncing}>
									Sync to Shopify
								</Button>
							</div>
						</SectionCard>
					</Col>
					
					<Col xs={24} lg={12}>
						<SectionCard title="Import from Shopify">
							<Paragraph>
								<Text>Import products from your Shopify store into your Xoe Med inventory. This will create new products or update existing ones.</Text>
							</Paragraph>
							<Divider />
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Text type="secondary">Import products from your Shopify store</Text>
								<Button type="primary" icon={<ImportOutlined />} onClick={handleImportProducts} loading={importing}>
									Import from Shopify
								</Button>
							</div>
						</SectionCard>
					</Col>
				</Row>

				{lastSyncResult && (
					<SectionCard title="Last Sync to Shopify Results">
						<Row gutter={16}>
							<Col xs={24} md={8}>
								<Statistic title="Products Created" value={lastSyncResult.created} valueStyle={{ color: 'var(--mg-success)' }} prefix={<SyncOutlined />} />
							</Col>
							<Col xs={24} md={8}>
								<Statistic title="Products Updated" value={lastSyncResult.updated} valueStyle={{ color: 'var(--mg-primary)' }} prefix={<SyncOutlined />} />
							</Col>
							<Col xs={24} md={8}>
								<Statistic title="Total Processed" value={lastSyncResult.created + lastSyncResult.updated} valueStyle={{ color: 'var(--mg-info)' }} prefix={<SyncOutlined />} />
							</Col>
						</Row>

						{lastSyncResult.errors && lastSyncResult.errors.length > 0 && (
							<Alert
								style={{ marginTop: 16 }}
								message="Sync Errors"
								description={
									<ul style={{ margin: 0, paddingLeft: 20 }}>
										{lastSyncResult.errors.map((err: any, idx: number) => (
											<li key={idx}>
												<Text code>{err.product_id || 'Unknown'}</Text>: {err.message}
											</li>
										))}
									</ul>
								}
								type="error"
								showIcon
							/>
						)}
					</SectionCard>
				)}

				{lastImportResult && (
					<SectionCard title="Last Import from Shopify Results">
						<Row gutter={16}>
							<Col xs={24} md={8}>
								<Statistic title="Products Created" value={lastImportResult.created} valueStyle={{ color: 'var(--mg-success)' }} prefix={<ImportOutlined />} />
							</Col>
							<Col xs={24} md={8}>
								<Statistic title="Products Updated" value={lastImportResult.updated} valueStyle={{ color: 'var(--mg-primary)' }} prefix={<ImportOutlined />} />
							</Col>
							<Col xs={24} md={8}>
								<Statistic title="Total Processed" value={lastImportResult.created + lastImportResult.updated} valueStyle={{ color: 'var(--mg-info)' }} prefix={<ImportOutlined />} />
							</Col>
						</Row>

						{lastImportResult.errors && lastImportResult.errors.length > 0 && (
							<Alert
								style={{ marginTop: 16 }}
								message="Import Errors"
								description={
									<ul style={{ margin: 0, paddingLeft: 20 }}>
										{lastImportResult.errors.map((err: any, idx: number) => (
											<li key={idx}>
												<Text code>{err.product_id || 'Unknown'}</Text>: {err.message}
											</li>
										))}
									</ul>
								}
								type="error"
								showIcon
							/>
						)}
					</SectionCard>
				)}

				<SectionCard title="How Product Sync Works">
					<Paragraph>
						<ul>
							<li><strong>Sync to Shopify:</strong> Creates or updates products in your Shopify store based on your Xoe Med inventory</li>
							<li><strong>Import from Shopify:</strong> Brings products from your Shopify store into your Xoe Med inventory</li>
							<li><strong>Product Matching:</strong> Products are matched by SKU or external ID to prevent duplicates</li>
							<li><strong>Inventory Updates:</strong> Stock levels and product details are synchronized between systems</li>
							<li><strong>Error Handling:</strong> Any sync issues are reported for manual review</li>
						</ul>
					</Paragraph>
				</SectionCard>
			</Space>
		</div>
	);
};

export default ProductSyncTab;